import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { CashierSalesReport } from '../api/types'
import { formatMoney } from '../ui/format'
import { Banner, Empty, ErrorBanner, Field, Panel, Spinner, TextInput } from '@shop/ui'

/** يومُ عملٍ محلّيٌّ بصيغة `YYYY-MM-DD`. */
function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date())
}

/**
 * كشفُ مبيعات الموظّفين — <b>من باع كم</b>.
 *
 * <p>
 * ⚠ <b>والمرتجعُ يُنسَب إلى من رحّله لا إلى من باع</b> (ب-٢): موظّفٌ يستقبل
 * مرتجعَ بيعةِ زميله يظهر عنده. والعملُ الذي وقع اليومَ عملُه — ولو نُسب
 * المرتجعُ إلى البائع الأصليّ لَتغيّر رقمُ يومٍ مضى بعد إقفاله.
 * </p>
 *
 * <p>
 * ⚠ <b>ونطاقُ التقرير يُطبَّق في الخادم</b>: من يملك «تقاريري» وحدَها يرى
 * سطرَه هو. وهذه الشاشةُ لا تفتح بابًا خلفيًّا لقراءة مبيعات الزملاء.
 * </p>
 */
export default function CashierSales() {
  const { can } = useAuth()
  const [from, setFrom] = useState(today())
  const [to, setTo] = useState(today())

  const report = useQuery({
    queryKey: ['cashier-sales', from, to],
    queryFn: () => api<CashierSalesReport>(
      `/api/v1/reports/cashiers?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
    enabled: can(Perm.reportViewMine) && from !== '' && to !== '',
  })

  const data = report.data ?? null

  return (
    <Panel title="مبيعاتُ الموظّفين">
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <Field label="من يوم">
          <TextInput value={from} onChange={setFrom} placeholder="2026-09-01" />
        </Field>

        <Field label="إلى يوم" hint="يومُ عملٍ بتوقيت عمّان — لا ساعاتُ التقويم">
          <TextInput value={to} onChange={setTo} placeholder="2026-09-30" />
        </Field>
      </div>

      <ErrorBanner error={report.error} />

      {report.isPending && <Spinner label="يُقرأ التقرير…" />}

      {data && data.rows.length === 0 && (
        <Empty>لا مبيعاتٍ في هذا المدى.</Empty>
      )}

      {data && data.rows.length > 0 && (
        <>
          <Banner tone="info">
            <b className="tabular" dir="ltr">{data.transactions}</b> فاتورةً
            {' · مبيعات '}<b>{formatMoney(data.revenue)}</b>
            {data.returned > 0 && <> · مرتجعات <b className="text-warn">{formatMoney(data.returned)}</b></>}
            {' · الصافي '}<b>{formatMoney(data.net)}</b>
          </Banner>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-ink-soft">
                <tr className="border-b border-line">
                  <th className="p-2 text-right">الموظّف</th>
                  <th className="p-2 text-left">فواتير</th>
                  <th className="p-2 text-left">المبيعات</th>
                  <th className="p-2 text-left">متوسّطُ الفاتورة</th>
                  <th className="p-2 text-left">نقدًا</th>
                  <th className="p-2 text-left">بطاقة وكليك</th>
                  <th className="p-2 text-left">مرتجعات</th>
                  <th className="p-2 text-left">الصافي</th>
                </tr>
              </thead>

              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.cashierId ?? 'none'} className="border-b border-line/50">
                    <td className="p-2">{row.cashierName}</td>
                    <td className="tabular p-2 text-left" dir="ltr">{row.transactions}</td>
                    <td className="tabular p-2 text-left" dir="ltr">{formatMoney(row.revenue)}</td>
                    <td className="tabular p-2 text-left text-ink-soft" dir="ltr">{formatMoney(row.basket)}</td>
                    <td className="tabular p-2 text-left" dir="ltr">{formatMoney(row.cashIn)}</td>
                    <td className="tabular p-2 text-left" dir="ltr">{formatMoney(row.nonCashIn)}</td>
                    <td className="tabular p-2 text-left" dir="ltr">
                      {row.returned > 0
                        ? <span className="text-warn">{formatMoney(row.returned)}</span>
                        : <span className="text-ink-soft">—</span>}
                    </td>
                    <td className="tabular p-2 text-left font-bold" dir="ltr">{formatMoney(row.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-2 text-xs text-ink-soft">
            المرتجعُ يُنسَب إلى من رحّله لا إلى من باع — فلا يتغيّر رقمُ يومٍ مضى.
          </p>
        </>
      )}
    </Panel>
  )
}
